import { useEffect, useMemo, useRef, useState } from 'react';

export default function SearchableSelect({
  options,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  getOptionSearchText,
  placeholder = 'Search...',
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);

  const selectedOption = options.find((o) => String(getOptionValue(o)) === String(value));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      (getOptionSearchText ? getOptionSearchText(o) : getOptionLabel(o)).toLowerCase().includes(q)
    );
  }, [options, query, getOptionSearchText, getOptionLabel]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectOption = (option) => {
    onChange(String(getOptionValue(option)));
    setQuery('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setIsOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlightedIndex]) selectOption(filtered[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={isOpen ? query : selectedOption ? getOptionLabel(selectedOption) : ''}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          setQuery('');
          setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
      />
      {isOpen && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg text-sm">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-gray-400">No matches</li>
          ) : (
            filtered.map((option, index) => {
              const isSelected = String(getOptionValue(option)) === String(value);
              return (
                <li
                  key={getOptionValue(option)}
                  onClick={() => selectOption(option)}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                    index === highlightedIndex ? 'bg-indigo-50' : ''
                  } ${isSelected ? 'font-medium text-indigo-600' : 'text-gray-900'}`}
                >
                  {getOptionLabel(option)}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
